import { inject, Injectable, signal } from '@angular/core'
import { FilterId, IFilter, IFilterCommands, IFilterSettings, IIsFilters, INotFilters } from '../interfaces/filter.interface'
import { IJsonLogic } from '../interfaces/json-logic.interface'
import { Buffer } from 'buffer'
import { jsonLogic } from '../utils/json-logic.util'
import { getDefaultFilters } from '../data/filters.data'
import { ClientService } from './client.service'
import { checkDrilldownFilterEmpty, getEmptyDrilldownFilterState } from 'src/app/components/filters/drilldown/drilldown-filter.component'
import { EventService } from './event.service'

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  clientService = inject(ClientService)
  eventService = inject(EventService)

  filterSettings: IFilterSettings = {}
  filters = signal<IFilter[]>([])
  customFilterLabels = signal<Record<FilterId, string>>({} as Record<FilterId, string>)
  customFields = signal<string[]>([])

  init() {
    const defaultFilters = getDefaultFilters()
    this.customFilterLabels.set(this.clientService.getClient()?.custom_fields ?? {} as Record<FilterId, string>)
    const customFields = Object.keys(this.clientService.getClient()?.custom_fields ?? {})
    this.customFields.set(customFields)
    const customFilters = [] as IFilter[]
    customFields.forEach(field => {
      customFilters.push({
        id: field as FilterId,
        state: getEmptyDrilldownFilterState(),
        isEmpty: checkDrilldownFilterEmpty,
      } as IFilter)
    })
    this.filters.set([...defaultFilters, ...customFilters])
  }

  private _is: IIsFilters = {
    listed: {
      ...jsonLogic.equals(
        jsonLogic.variable('filter_is_listed'),
        'true',
      )
    },
    featured: {
      ...jsonLogic.equals(
        jsonLogic.variable('filter_is_featured'),
        'true',
      )
    },
    equipment: (id: string) => ({
      ...jsonLogic.equals(
        jsonLogic.variable('equipment_number'),
        id,
      )
    }),
  }

  private _not: INotFilters = {
    featured: {
      ...jsonLogic.doesNotEqual(
        jsonLogic.variable('filter_is_featured'),
        'true',
      )
    },
    equipment: (id: string) => ({
      ...jsonLogic.doesNotEqual(
        jsonLogic.variable('equipment_number'),
        id,
      )
    }),
  }

  build(
    processor: (commands: IFilterCommands) => IJsonLogic,
  ) {
    const result = processor({
      and: jsonLogic.and,
      or: jsonLogic.or,
      inArray: jsonLogic.inArray,
      variable: jsonLogic.variable,
      equals: jsonLogic.equals,
      doesNotEqual: jsonLogic.doesNotEqual,
      lt: jsonLogic.lt,
      lte: jsonLogic.lte,
      gt: jsonLogic.gt,
      gte: jsonLogic.gte,
      is: this._is,
      not: this._not,
    })
    return result
  }

  hasAnyFilters(): boolean {
    return this.filters().some(f => !f.isEmpty(f.state))
  }

  hasFilter(filterId: FilterId): boolean {
    const filter = this.filters().find(f => f.id === filterId)
    if (filter) {
      return !filter.isEmpty(filter.state)
    }
    return false
  }

  getFiltersWithState() {
    return this.filters().filter(f => !f.isEmpty(f.state))
  }

  loadFilters(encodedFiltersData: string | null): void {
    if (encodedFiltersData) {
      const decodedFilters: IFilter[] = this.decodeFilters(encodedFiltersData) ?? []
      decodedFilters.forEach(decodedFilter => {
        this.updateFilter(decodedFilter.id ?? null, decodedFilter.state)
      })
    }
  }

  updateFilter(filterId: FilterId | null, newFilterState: any): void {
    this.filters.update(filters => {
      const filter = filters.find(f => f.id === filterId)
      if (filter) {
        filter.state = newFilterState
      }
      return filters
    })
  }

  clearFilterState(): void {
    this.filters.set(getDefaultFilters())
  }

  getFilter(filterId: FilterId): IFilter {
    return this.filters().find(f => f.id === filterId) ?? { id: FilterId.Unknown, state: [], isEmpty: () => true } as IFilter
  }

  getCustomFilterLabel(filterId: FilterId): string {
    return this.customFilterLabels()[filterId] ?? filterId
  }

  encodeFilters(filters: IFilter[]): string {
    const encoder = new TextEncoder()
    const bytes = encoder.encode(JSON.stringify(filters))

    // Convert the Uint8Array to a Base64 string
    return btoa(String.fromCharCode(...bytes))
  }

  decodeFilters(filters: string): any {
    const buffer = Buffer.from(filters, 'base64')
    const str = buffer.toString('utf-8')
    return JSON.parse(str)
  }
}
